import { Engine } from "../core/Engine";
import type { Scene, InputAction } from "../core/types";
import { games } from "../games/registry";
import { parseHash, goHub } from "./routes";
import { HubView } from "./ui/HubView";
import { renderHUD, clearHUD } from "./ui/HUD";
import { getBestScore, setBestScore } from "../core/Storage";
import { Api } from "../core/Api";

export class App {
  private engine: Engine;
  private ctx: CanvasRenderingContext2D;
  private currentScene?: Scene;

  constructor(private deps: { canvas: HTMLCanvasElement; uiRoot: HTMLDivElement }) {
    this.engine = new Engine(deps.canvas);
    this.ctx = this.engine.getContext();
  }

  start() {
    this.engine.mountInput();

    window.addEventListener("hashchange", () => this.navigate());
    this.navigate();

    const input = this.engine.getInput();
    input.on((a) => this.onInput(a));

    this.engine.start((dt) => {
      this.currentScene?.update(dt);
      this.currentScene?.render(this.ctx);
    });
  }

  private onInput(action: InputAction) {
    (this.currentScene as any)?.onInput?.(action);
  }

  private setScene(scene: Scene) {
    this.currentScene?.unmount();
    this.currentScene = scene;
    this.currentScene.mount();
  }

  private navigate() {
    const route = parseHash();

    if (route.name === "hub") {
      clearHUD();
      const hub = new HubScene(this.deps.uiRoot, games);
      this.setScene(hub);
      return;
    }

    const module = games.find((g) => g.id === route.gameId);
    if (!module) {
      goHub();
      return;
    }

    const game = module.create();
    const gameScene = new GameScene(this.deps.uiRoot, module.id, module.title, game);
    this.setScene(gameScene);
  }
}

class HubScene implements Scene {
  type: "hub" = "hub";
  private view: HubView;

  constructor(private uiRoot: HTMLElement, modules: typeof games) {
    this.view = new HubView(uiRoot, modules);
  }

  mount() {
    this.view.mount();

    // Optionnel (plus tard) : récupérer l’utilisateur connecté pour afficher dans le hub
    // Api.me().then(() => {}).catch(() => {});
  }

  unmount() {
    this.view.unmount();
  }

  update(_dt: number) {}

  render(ctx: CanvasRenderingContext2D) {
    const { width: w, height: h } = ctx.canvas;
    ctx.clearRect(0, 0, w, h);

    ctx.globalAlpha = 1;
    ctx.fillStyle = "rgba(255,255,255,0.04)";
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = "rgba(34,197,94,0.08)";
    ctx.beginPath();
    ctx.arc(w * 0.15, h * 0.25, Math.min(w, h) * 0.22, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.beginPath();
    ctx.arc(w * 0.85, h * 0.65, Math.min(w, h) * 0.28, 0, Math.PI * 2);
    ctx.fill();
  }
}

class GameScene implements Scene {
  type: "game" = "game";
  private panel?: HTMLDivElement;

  private lastStatus: "ready" | "running" | "gameover" = "ready";
  private scoreSubmitted = false;

  private jobs: Array<() => Promise<void>> = [];
  private jobRunning = false;

  constructor(
    private uiRoot: HTMLElement,
    private gameId: string,
    private title: string,
    private game: any
  ) {}

  mount() {
    this.game.reset();
    this.lastStatus = "ready";
    this.scoreSubmitted = false;
    this.jobs = [];
    this.jobRunning = false;

    const panel = document.createElement("div");
    panel.className = "ui-panel";
    panel.style.maxWidth = "900px";
    panel.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
        <div>
          <div style="font-weight:800;font-size:18px;">${this.title}</div>
          <div style="color:var(--muted);font-size:13px;margin-top:2px;">
            Espace / clic pour démarrer.
          </div>
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn" id="btn-retry">Rejouer</button>
          <button class="btn" id="btn-hub">Retour</button>
        </div>
      </div>
    `;
    panel.querySelector("#btn-retry")!.addEventListener("click", () => this.resetGame());
    panel.querySelector("#btn-hub")!.addEventListener("click", () => (location.hash = ""));
    this.uiRoot.appendChild(panel);
    this.panel = panel;
  }

  unmount() {
    this.panel?.remove();
    this.panel = undefined;
    clearHUD();
  }

  private resetGame() {
    this.game.reset();
    this.lastStatus = "ready";
    this.scoreSubmitted = false;
    this.jobs = [];
    this.jobRunning = false;
  }

  update(dt: number) {
    this.game.update(dt);
    this.runJobs();

    const state = this.game.getState();

    if (state.status === "running") {
      this.lastStatus = "running";
      this.scoreSubmitted = false;
      renderHUD(this.uiRoot, `Score: ${state.score}  •  Record: ${getBestScore(this.gameId)}`);
      return;
    }

    if (state.status === "gameover") {
      const bestLocal = setBestScore(this.gameId, state.score);

      if (this.lastStatus !== "gameover") {
        this.lastStatus = "gameover";
        this.scoreSubmitted = false;
      }

      // Envoi serveur : 1 seule fois par Game Over
      if (!this.scoreSubmitted) {
        this.scoreSubmitted = true;

        this.enqueue(async () => {
          try {
            // Si non connecté, ton API doit renvoyer 401 -> on ignore silencieusement
            await Api.submitScore(this.gameId, state.score);
          } catch {
            // Ne jamais casser l’expérience
          }
        });
      }

      renderHUD(
        this.uiRoot,
        `Game Over — Score: ${state.score}  •  Record: ${bestLocal}  (Espace pour rejouer)`
      );
      return;
    }

    this.lastStatus = "ready";
    renderHUD(this.uiRoot, `Prêt — Espace / clic pour démarrer`);
  }

  render(ctx: CanvasRenderingContext2D) {
    this.game.render(ctx);
  }

  onInput(action: InputAction) {
    this.game.onAction(action);
  }

  private enqueue(job: () => Promise<void>) {
    this.jobs.push(job);
  }

  private runJobs() {
    if (this.jobRunning) return;
    const job = this.jobs.shift();
    if (!job) return;

    this.jobRunning = true;
    job()
      .catch(() => {})
      .finally(() => {
        this.jobRunning = false;
      });
  }
}
